import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import { AccountContext } from '../../../../components/AccountContext';
import {
  ESSENTIAL_ACCOUNT_TO_BE_DELETED_NOTIFICATION_QUERY,
  PAY_RETENTION_FEE_MUTATION,
} from '../../services/dashboard.service';
import { growlActionSuccess, growlErrors } from '../../../../utils/NotificationUtil';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import InsufficientCreditsForRetentionFee from './InsufficientCreditsForRetentionFee';
import AccountToBeDeletedNotification from './AccountToBeDeletedNotification';

const DEFAULT_STATE = {
  payedBy: null,
  numberOfDaysUntilDeletion: 0,
  retentionPrice: 0,
  creditBalance: 0,
  canPayRetentionFee: false,
  gpiRepresentative: {
    parentAccountOwnerFullName: '',
    parentAccountOwnerEmail: '',
  },
};

const INSUFFICIENT_CREDITS_DEFAULT_STATE = {
  payedBy: null,
  numberOfDaysUntilDeletion: 0,
  retentionPrice: 0,
  creditBalance: 0,
  gpiRepresentative: {
    parentAccountOwnerFullName: '',
    parentAccountOwnerEmail: '',
  },
};

const EssentialAccountToBeDeletedNotification = () => {
  const [notification, setNotification] = useState(DEFAULT_STATE);
  const [insufficientCreditsData, setInsuffiecientCreditsData] = useState(INSUFFICIENT_CREDITS_DEFAULT_STATE);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const { data } = useQuery(ESSENTIAL_ACCOUNT_TO_BE_DELETED_NOTIFICATION_QUERY, {
    variables: {
      accountId: currentAccount,
    },
    fetchPolicy: 'network-only',
  });
  const [payRetentionFeeMutation, { loading }] = useMutation(PAY_RETENTION_FEE_MUTATION);

  useEffect(() => {
    if (data && data.essentialAccountToBeDeletedNotification) {
      setNotification(data.essentialAccountToBeDeletedNotification);
    }
  }, [data]);

  const payRetentionFee = async () => {
    try {
      await payRetentionFeeMutation({
        variables: {
          accountId: currentAccount,
        },
        refetchQueries: ['essentialAccountDashboardCreditsInformationQuery'],
      });

      growlActionSuccess(growl);
      setNotification(DEFAULT_STATE);
      history.push(`/accounts/${currentAccount}`);
    } catch (error) {
      const errorCode = error.graphQLErrors[0].extensions.errorCode;

      if (!!errorCode && errorCode === 'RETENTION_FEE_PAYMENT_FAILED') {
        setNotification(DEFAULT_STATE);
        setInsuffiecientCreditsData({
          payedBy: notification.payedBy,
          numberOfDaysUntilDeletion: notification.numberOfDaysUntilDeletion,
          retentionPrice: notification.retentionPrice,
          creditBalance: notification.creditBalance,
          gpiRepresentative: notification.gpiRepresentative,
        });
      } else {
        growlErrors(growl);
      }
    }
  };

  const accountToBeDeletedNotificationDialogIsVisible = notification !== DEFAULT_STATE;
  const insufficientCreditsDataDialogIsVisible = insufficientCreditsData !== INSUFFICIENT_CREDITS_DEFAULT_STATE;

  if (notification !== DEFAULT_STATE) {
    return (
      <AccountToBeDeletedNotification
        visible={accountToBeDeletedNotificationDialogIsVisible}
        notification={notification}
        payRetentionFee={payRetentionFee}
        mutationIsLoading={loading}
        hide={() => setNotification(DEFAULT_STATE)}
      />
    );
  }

  if (insufficientCreditsDataDialogIsVisible) {
    return (
      <InsufficientCreditsForRetentionFee
        visible={insufficientCreditsDataDialogIsVisible}
        insufficientCreditsData={insufficientCreditsData}
      />
    );
  }

  return null;
};

export default EssentialAccountToBeDeletedNotification;
